/**
 * Thin bar that pins above the editor whenever ≥1 AI proposals are pending.
 * Bulk accept/reject for the impatient nurse. Disappears when the queue empties.
 */
import { Button, cn } from '@seen/ui'
import {
  IconCheck,
  IconEyeOff,
  IconRefresh,
  IconSparkles,
  IconX,
} from '@tabler/icons-react'
import type { Editor } from '@tiptap/react'
import { useEditorState } from '@tiptap/react'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import {
  AI_PROPOSALS_PLUGIN_KEY,
  type AIProposal,
} from '#src/components/rich-text-editor/AIProposalsExtension.ts'
import { withSingleUndoStep } from '#src/components/rich-text-editor/bulkUndoGrouping.ts'
import { DroppedSuggestionsDialog } from '#src/components/rich-text-editor/DroppedSuggestionsDialog.tsx'
import type { DroppedSuggestion } from '#src/hooks/useAISuggestionHydration.ts'

export function AIProposalStatusBar({
  editor,
  noteSuggestionsEnabled,
  dropped = [],
  pendingToolOpCount = 0,
  pendingQuestionnaireCount = 0,
  pendingValidatedToolCount = 0,
  acceptSuggestion,
  onAcceptAllQuestionnaires,
  onAcceptAllValidatedTools,
  onReject,
  onRejectAll,
  onUndoRejectAll,
  onHide,
  stickyTopClassName = 'top-4',
}: {
  editor: Editor | null
  /*
   * The campaign gate (required, no default, so a missed call site fails to
   * compile). Off: the drop count still shows — drops predate the campaign and
   * happen on every AI-edited editor — but as the plain line it was before,
   * with no summary control and no `DroppedSuggestionsDialog`. Nothing else in
   * this bar is gated: accept/reject, Hide, and the pending counts are shared
   * with the AI chat's inline edits.
   */
  noteSuggestionsEnabled: boolean
  /**
   * Pending suggestions that couldn't be applied — a lost anchor or unplaceable
   * text, or a condition that is no longer valid, detected either at reveal or
   * at accept. Summarized by a single control here that opens
   * `DroppedSuggestionsDialog`, so the RN knows non-placement is recoverable,
   * not data loss (design Q3), without the listing crowding out the note.
   */
  dropped?: DroppedSuggestion[]
  /**
   * Pending validated-tool suggestions (op type `proposeValidatedTool`) for this
   * note. These never paint as editor decorations — the validated-tool node view
   * reads each pending record straight off the note to drive its own banner +
   * prefilled form. They ARE counted into the staged-suggestions banner's total
   * (`pendingSuggestions.length`, all op types), so the status bar must add them
   * to the decoration count or the two would disagree post-reveal (Claude task
   * #9: 51 staged → 51 reviewing). Validated-tool records are decided
   * subscale by subscale in their own node view AND swept by the bulk controls
   * here, as the care plan's questionnaire atoms are.
   */
  pendingToolOpCount?: number
  /**
   * The questionnaire slice of `pendingToolOpCount` — undecided care-plan
   * answer atoms. Used only to decide whether the bulk controls are shown, and
   * deliberately NOT added to any total: these atoms are already counted once
   * in `pendingToolOpCount`, and adding them again would make the bar
   * double-count the very thing it shows. Without it a care plan whose whole
   * review is questionnaire answers rendered no "Accept all" or "Reject all"
   * at all, because nothing had painted a decoration.
   */
  pendingQuestionnaireCount?: number
  /**
   * The validated-tool slice of `pendingToolOpCount` — undecided subscales of a
   * staged validated tool. Like `pendingQuestionnaireCount` it only decides
   * whether the bulk controls are shown and is deliberately NOT added to any
   * total, since `pendingToolOpCount` already counts these once. Without it a
   * note whose only review is a staged Braden rendered no "Accept all" or
   * "Reject all" at all, because nothing had painted a decoration.
   */
  pendingValidatedToolCount?: number
  /**
   * The full accept pipeline (design §1.3) for a single suggestion. "Accept all"
   * routes every proposal through it SEQUENTIALLY, so each re-resolves against
   * the doc the previous insert produced; the pipeline owns the Q5 re-read guard,
   * the recheck→dropped routing, the receipt, and the FHIR side effects. It is
   * the only accept path — there is no decoration-only bulk fallback.
   */
  acceptSuggestion: (id: string) => void | Promise<void>
  /**
   * Accept every still-undecided care-plan questionnaire answer, the half of
   * "Accept all" that no decoration stands for. Run after the inline pipeline
   * so both halves of the sweep are in, it resolves to the restore that takes
   * the answers back out again — or to null when it wrote none, and there is
   * nothing to arm an undo with. Absent on surfaces with no questionnaire.
   */
  onAcceptAllQuestionnaires?: () => Promise<(() => void) | null>
  /**
   * Accept every still-undecided subscale of every staged validated tool, the
   * other half of "Accept all" that no decoration stands for. Synchronous: it
   * writes staging decisions only. It deliberately does NOT submit the tool's
   * form — accepting fills the values in, and the QuestionnaireResponse is
   * still created by the clinician pressing Submit. Returns the restore that
   * takes those subscales back to undecided, or null when it decided none.
   */
  onAcceptAllValidatedTools?: () => (() => void) | null
  /**
   * Persist EXPLICIT clinician rejects for PLACED suggestions (status='rejected'
   * via the debounced projection). Fired by single-proposal reject paths. NOT
   * used by Reject All — use onRejectAll for the bulk path.
   */
  onReject?: (ids: string[]) => void
  /**
   * Persist EXPLICIT clinician rejects for ALL pending suggestions — both
   * placed (decorated) and unplaced (droppedCount) — so a Reject All correctly
   * dismisses every record regardless of whether it could be anchored. Absent →
   * no persistence (non-note editor). When provided, called instead of onReject
   * by the Reject All button.
   *
   * Receives the placed ids snapshotted from the plugin, unions them with any
   * unplaced ids the host knows about, rejects all of them, and RETURNS the
   * full set of rejected ids (placed + unplaced). The status bar stores this
   * full set so the "Undo rejection of all" button can re-pend every dismissed
   * record, not just the placed ones.
   */
  onRejectAll?: (placedIds: string[]) => string[]
  /**
   * Re-project all batch-rejected suggestion ids back to 'pending'. Called by
   * both undo affordances the sweep arms — the inline "Undo rejection of all"
   * button and the Cmd+Z entry. For unplaced suggestions this restores them to
   * the droppedCount listing. It settles the RECORDS only; the highlights the
   * sweep tore down are repainted here from the snapshot the bar kept (see
   * `rejectedProposalsRef`). Absent → no undo button shown (non-note editor).
   */
  onUndoRejectAll?: (ids: string[]) => void
  /**
   * Return the revealed suggestion review to the deferred (banner) state without
   * deciding anything (Claude task #4). The host tears down the painted
   * decorations via the remove-proposals path and flips its per-tab `revealed`
   * flag back to false, re-showing `StagedSuggestionsBanner`. Records stay
   * PENDING — Hide is un-paint, not reject. Omit on surfaces that never defer
   * (the control is hidden when absent).
   */
  onHide?: () => void
  /**
   * Sticky offset from the top of the bar's scroll container. Defaults to
   * `top-4`, which suits surfaces whose editor owns its own scroll viewport
   * (e.g. the full-mode visit note, where no other sticky header shares the
   * scroller). Surfaces that render the editor in the shared page ScrollArea —
   * which also holds the `sticky top-0 z-50` patient nav header — must pass a
   * larger offset so the bar pins *below* that header instead of behind it
   * (its `z-20` loses to the header's `z-50`). See the care-plan page.
   */
  stickyTopClassName?: string
}): ReactNode {
  const count = useEditorState({
    editor,
    selector: ({ editor: e }) => {
      if (!e) {
        return 0
      }
      return AI_PROPOSALS_PLUGIN_KEY.getState(e.state)?.proposals.size ?? 0
    },
  })

  /* IDs of the most recent Reject All batch, stored so the undo button can
   * re-project them. null = no batch pending undo. Disarmed on any real doc
   * change (mirrors the disarm-on-edit semantics of AIProposalUndoExtension)
   * and on unmount. The status bar owns the id list because the batch includes
   * suggestions with no plugin-state entry — unplaced records, and the care
   * plan's questionnaire atoms — that the editor extension can't know about;
   * the extension holds only the one-press undo entry that restores it. */
  const [rejectedBatch, setRejectedBatch] = useState<string[] | null>(null)

  /* The decorations that batch tore down, held beside its id list so its undo
   * puts the highlights back with the records. Re-pending a record does NOT
   * repaint it: the hydration paint is a one-shot per (editor, resource,
   * generation) and a record returning to pending re-arms none of those keys,
   * which is why the inline proposals used to come back only on a reload while
   * the questionnaire cards — ordinary React off the same records — returned at
   * once. The snapshot still addresses the live note because the batch disarms
   * on any document change, the same property that lets a single reject's undo
   * re-add its stored proposal. */
  const rejectedProposalsRef = useRef<AIProposal[]>([])

  /* The most recent Accept All, armed the same way so the sweep has a button
   * as well as a key. `restore` is the host's compensating write for what the
   * sweep decided outside the editor; `withDocUndo` says the inline half
   * changed the document. Only one of the two batches is ever armed: each
   * sweep clears the other. */
  const [acceptedBatch, setAcceptedBatch] = useState<{
    restore: () => void
    withDocUndo: boolean
  } | null>(null)

  const [isDroppedDialogOpen, setIsDroppedDialogOpen] = useState(false)

  /* Track the editor's doc node so we can detect forward edits and disarm the
   * undo batch (reject-all → type → "Undo rejection" should NOT fire).
   * ProseMirror replaces state.doc with a new Node on every doc-mutating
   * transaction, so reference inequality is a reliable change signal. Decoration
   * transactions (which carry no doc change) keep the same doc reference and do
   * NOT disarm the batch. */
  const docNodeRef = useRef<object | null>(null)
  useEffect(() => {
    if (!editor || (rejectedBatch === null && acceptedBatch === null)) {
      return
    }
    const onTransaction = (): void => {
      const currentDoc = editor.state.doc
      if (docNodeRef.current !== null && currentDoc !== docNodeRef.current) {
        setRejectedBatch(null)
        setAcceptedBatch(null)
      }
      docNodeRef.current = currentDoc
    }
    editor.on('transaction', onTransaction)
    return (): void => {
      editor.off('transaction', onTransaction)
    }
  }, [editor, rejectedBatch, acceptedBatch])

  /* Decorations painted in THIS editor. "Accept all"/"Reject all" operate on
   * exactly these. */
  const liveCount = count ?? 0
  const droppedCount = dropped.length

  // Dismissing the last row hides the dialog by count alone, but the open state
  // would survive it — and the summary control that sets it is gone by then, so
  // a later unplaceable suggestion would re-open the dialog with no click.
  useEffect(() => {
    if (droppedCount === 0) {
      setIsDroppedDialogOpen(false)
    }
  }, [droppedCount])

  /* Dropped records that place cleanly against the note as it now stands — the
   * clinician fixed whatever made them unplaceable. Carrying `place` IS the
   * recovered flag (see DroppedSuggestion); one control paints all of them,
   * because a suggestion that is placeable and still pending is one the
   * clinician wants painted. */
  const recovered = dropped.filter((item) => item.place !== undefined)
  /* The complement, and the count the "couldn't be placed" summary shows. A
   * recovered record is placeable NOW, so counting it as unplaceable made the
   * bar contradict itself — "1 suggestion couldn't be placed" beside "Place 1
   * recovered suggestion", the same record described twice. Each control counts
   * only its own subset; when every drop has recovered, the placement problem
   * has resolved and only the Place control shows. */
  const unrecoverable = dropped.filter((item) => item.place === undefined)
  /* The number the RN sees, matching the staged-suggestions banner: painted
   * decorations PLUS pending validated-tool records (which never paint). Keeps
   * banner and status bar in agreement (task #9). */
  const totalPending = liveCount + pendingToolOpCount
  /* The reject-all batch describes this bar only while nothing is pending.
   * Staging a fresh AI run paints new cards through DECORATION transactions,
   * which the disarm latch above deliberately ignores (it exists to survive
   * decoration churn), so on its own the batch would keep the bar reading "All
   * suggestions rejected" over live suggestions and offering only an undo —
   * with no Accept all / Reject all to act on them, until a reload cleared the
   * ephemeral state. Live pending work therefore wins the message and the bulk
   * controls; the batch keeps only its own undo button, which stays offered
   * beside them rather than instead of them. */
  const isBatchRejectedView = rejectedBatch !== null && totalPending === 0
  /* Its inverse. Drops stay pending through an Accept all (a reject sweep
   * dismisses them; an accept sweep cannot place them), so while any remain
   * the bar keeps reporting them and the undo sits beside that instead. */
  const isBatchAcceptedView =
    acceptedBatch !== null && totalPending === 0 && droppedCount === 0
  if (
    !editor ||
    (totalPending === 0 &&
      droppedCount === 0 &&
      rejectedBatch === null &&
      acceptedBatch === null)
  ) {
    return null
  }

  /* Restore a whole "Reject all", from either affordance: put every record the
   * sweep decided back to pending, then repaint the proposals it un-painted, so
   * one press restores what is on screen as well as what is in the record.
   * Records first — a decoration painted ahead of its record would sit, for as
   * long as the projection takes, over a record the decided-sweep still reads
   * as rejected. */
  const undoRejectAll = (ids: string[]): void => {
    onUndoRejectAll?.(ids)
    const repaint = rejectedProposalsRef.current
    rejectedProposalsRef.current = []
    if (repaint.length > 0) {
      editor.commands.addAIProposals(repaint)
    }
    setRejectedBatch(null)
  }

  /* Restore a whole "Accept all" from the button. The sweep's entry in the
   * undo extension is the same one Cmd+Z would pop, and taking it THROUGH the
   * extension is what keeps the two affordances one: the entry is spent, so
   * the key cannot restore the sweep a second time, and the redo latch is
   * armed, so Cmd+Shift+Z cannot replay the prose without the answers. The
   * extension records an entry only when the sweep decided something outside
   * the editor; a sweep of inline proposals alone is one grouped document
   * step, and the document's own undo is the whole of its reversal. */
  const undoAcceptAll = (batch: {
    restore: () => void
    withDocUndo: boolean
  }): void => {
    const handled = editor.commands.undoAIProposalBatch?.() ?? false
    if (!handled) {
      if (batch.withDocUndo) {
        editor.commands.undo?.()
      }
      batch.restore()
    }
    setAcceptedBatch(null)
  }

  const acceptAll = (): void => {
    setRejectedBatch(null)
    setAcceptedBatch(null)
    /* Any earlier reject entry is spent the moment another bulk control runs;
     * leaving it poppable would make the Cmd+Z after an Accept all re-pend one
     * unrelated suggestion rejected minutes ago. */
    editor.commands.clearAIProposalRejectStacks?.()
    // Snapshot ids first since accepting mutates the plugin map.
    const ids = Array.from(
      AI_PROPOSALS_PLUGIN_KEY.getState(editor.state)?.proposals.keys() ?? []
    )
    const docBefore = editor.state.doc
    /* Note editors: route each proposal through the full accept pipeline (§1.3),
     * SEQUENTIALLY so each re-resolves against the doc the prior insert produced
     * rather than racing concurrent inserts. The pipeline owns the Q5 guard, the
     * recheck, the receipt, and side effects.
     *
     * Grouped into ONE undo step (withSingleUndoStep): each pipeline run is its
     * own history-tracked transaction separated by FHIR round trips, so without
     * the grouping how many times a clinician must press Cmd+Z to take back one
     * "Accept all" is decided by network latency. The bulk command this path
     * replaced applied every proposal in a single transaction; this restores that
     * property without giving up the pipeline. */
    void (async () => {
      let undone = false
      await withSingleUndoStep(editor, async (batch) => {
        for (const id of ids) {
          /* Cmd+Z landed while the batch was still applying. It took back every
           * accept so far (they are one undo step), and the accepts left in this
           * loop would put content back into the note the clinician just cleared.
           * The reconciler owns returning what was undone to pending; this only
           * has to stop adding to it. */
          if (batch.undone) {
            undone = true
            return
          }
          await acceptSuggestion(id)
        }
      })
      /* The clinician has already taken the sweep back, so the questionnaire
       * answers must not be written after it. */
      if (undone) {
        return
      }
      /* Both unpainted halves of the sweep, taken back by ONE undo entry: a
       * clinician who presses Accept all once presses undo once, whether the
       * note carried questionnaire answers, validated-tool subscales, or
       * both. */
      const restores = [
        (await onAcceptAllQuestionnaires?.()) ?? null,
        onAcceptAllValidatedTools?.() ?? null,
      ].filter((r): r is () => void => r !== null)
      /* `withDocUndo` says whether there is a document half to take back —
       * measured from the doc itself rather than from the id count, because a
       * proposal that dropped at accept leaves the note untouched, and undoing
       * a step this sweep never made would reach into the clinician's own
       * earlier typing. */
      const withDocUndo = editor.state.doc !== docBefore
      if (restores.length === 0 && !withDocUndo) {
        // The sweep changed nothing, so there is nothing to offer back.
        return
      }
      const restore = (): void => {
        for (const r of restores) {
          r()
        }
      }
      /* Arm the button, mirroring Reject all. Recorded only now: the accepts
       * above are forward document edits, and the disarm latch would have
       * wiped a batch armed before them. */
      docNodeRef.current = editor.state.doc
      setAcceptedBatch({ restore, withDocUndo })
      if (restores.length === 0) {
        return
      }
      /* And the key. The extension's entry exists only for a sweep with a half
       * outside the editor: an inline-only sweep is one grouped document step
       * that the document's own undo already takes back in one press. */
      editor.commands.recordAcceptedAIProposalBatch?.(restore, {
        withDocUndo,
      })
    })()
  }

  return (
    <output
      aria-live="polite"
      // Opts this surface out of the island's click-outside dismiss: "Accept
      // all" reads OPEN islands' live content, so a pointerdown here must not
      // unmount them first.
      data-ai-suggestion-surface
      className={cn(
        'sticky z-20 flex flex-col gap-2 rounded-md border border-purple-200 bg-purple-50 px-3 py-2 text-sm shadow-sm dark:border-purple-900 dark:bg-purple-950',
        stickyTopClassName
      )}
    >
      {/* Message and actions share a wrapping row, the way the repo's other
       * sticky toolbars do. The message keeps a real minimum width: with a
       * zero basis it never forced a wrap, so a wide action group (four
       * controls after a Reject all is undone) squeezed it to one word per
       * line and then overflowed into it. Once the actions no longer fit
       * beside a readable message they drop to their own line, right-aligned
       * by `ml-auto`; the message wraps its own text within whatever is left. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="flex max-w-full min-w-72 flex-1 items-center gap-2 text-purple-900 dark:text-purple-200">
          <IconSparkles className="size-4 shrink-0" />
          <span className="min-w-0">
            {isBatchRejectedView ? (
              <>All suggestions rejected.</>
            ) : isBatchAcceptedView ? (
              <>All suggestions accepted.</>
            ) : totalPending > 0 ? (
              <>
                Seen AI proposed {totalPending} change
                {totalPending === 1 ? '' : 's'}.
                {/* Inline-decoration proposals carry their own affordances, so
                 * they need no instruction here. So do the questionnaire's
                 * answer cards. Validated-tool suggestions paint no highlight,
                 * so when they are the only pending work the count alone would
                 * leave nowhere to look — those keep a pointer. */}
                {liveCount + pendingQuestionnaireCount > 0
                  ? null
                  : ' Review the highlighted tools below.'}
              </>
            ) : (
              <>Seen AI has suggestions for this note.</>
            )}
          </span>
        </div>
        {/* Never narrower than its buttons (every Button is whitespace-nowrap,
         * so shrinking meant overlapping). Capped at the container so that
         * when the actions alone outgrow it — deep browser zoom — they wrap
         * onto several lines instead of running off the edge. */}
        <div className="ml-auto flex max-w-full shrink-0 flex-wrap items-center justify-end gap-2">
          {rejectedBatch !== null && onUndoRejectAll && (
            /* Post-Reject-All undo affordance. Disappears on any doc change
             * (disarm latch) or when another action clears rejectedBatch. It is
             * the only control while the batch is all there is to say; once new
             * suggestions are pending it sits alongside them instead. */
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                undoRejectAll(rejectedBatch)
                /* The batch is spent. Drop the entry the Reject All armed, or
                 * the next Cmd+Z would restore it a second time. */
                editor.commands.clearAIProposalRejectStacks?.()
              }}
            >
              <IconX className="size-3.5" />
              Undo rejection of all
            </Button>
          )}
          {acceptedBatch !== null && (
            /* Its inverse: offered until a forward edit disarms it or another
             * sweep supersedes it, alone while the sweep is all there is to
             * say and beside the bulk controls once new work is pending. */
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => undoAcceptAll(acceptedBatch)}
            >
              <IconCheck className="size-3.5" />
              Undo acceptance of all
            </Button>
          )}
          {!isBatchRejectedView && !isBatchAcceptedView && (
            <>
              {onHide && (
                /* Return to the deferred banner without deciding (task #4): the host
                 * un-paints the decorations and re-shows StagedSuggestionsBanner. */
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={onHide}
                >
                  <IconEyeOff className="size-3.5" />
                  Hide
                </Button>
              )}
              {/* Shown for anything a sweep would decide, painted or not: a care
               * plan whose review is entirely questionnaire answers gets the
               * same two controls as a note full of inline proposals. */}
              {liveCount +
                pendingQuestionnaireCount +
                pendingValidatedToolCount >
                0 && (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      /* Snapshot the placed proposals before clearing decorations
                       * so we have their AIProposal objects. We'll also need the
                       * full set of pending IDs (placed + unplaced) for onRejectAll
                       * and the undo batch — those come from the caller via
                       * onRejectAll which receives all pending ids. */
                      const placedProposals = Array.from(
                        AI_PROPOSALS_PLUGIN_KEY.getState(
                          editor.state
                        )?.proposals.values() ?? []
                      )
                      const placedIds = placedProposals.map((p) => p.id)
                      setAcceptedBatch(null)
                      editor
                        .chain()
                        .focus(null, { scrollIntoView: false })
                        .rejectAllAIProposals()
                        .run()
                      // Clear single-reject undo entries so Cmd+Z after Reject All
                      // delegates to the doc UndoManager rather than partially
                      // restoring one earlier individually-rejected suggestion.
                      // Optional-call: absent on non-note editors.
                      editor.commands.clearAIProposalRejectStacks?.()
                      // Persist rejects for ALL pending records (placed + unplaced).
                      // onRejectAll is preferred when present (note editors); fall
                      // back to onReject for bare editors that only need placed ids.
                      // onRejectAll returns the FULL set of rejected ids (placed +
                      // unplaced) so rejectedBatch can re-pend everything on undo.
                      /* Arm the one-press undo for the whole sweep: the
                       * inline button and a Cmd+Z restore the same batch. The
                       * extension's keymap sits above the document
                       * UndoManager's, so while this entry stands the key
                       * brings the batch back instead of reaching past it into
                       * the note's own history. Optional-call: bare editors
                       * carry no AIProposalUndoExtension, and there the button
                       * remains the only affordance. */
                      const arm = (ids: string[]): void => {
                        docNodeRef.current = editor.state.doc
                        setRejectedBatch(ids)
                        rejectedProposalsRef.current = placedProposals
                        editor.commands.recordRejectedAIProposalBatch?.(() => {
                          undoRejectAll(ids)
                        })
                      }
                      if (onRejectAll) {
                        const allRejectedIds = onRejectAll(placedIds)
                        if (onUndoRejectAll) {
                          arm(allRejectedIds)
                        }
                      } else {
                        if (placedIds.length > 0) {
                          onReject?.(placedIds)
                        }
                        if (onUndoRejectAll) {
                          arm(placedIds)
                        }
                      }
                    }}
                  >
                    <IconX className="size-3.5" />
                    Reject all
                  </Button>
                  <Button type="button" size="sm" onClick={acceptAll}>
                    <IconCheck className="size-3.5" />
                    Accept all
                  </Button>
                </>
              )}
            </>
          )}
        </div>
      </div>
      {droppedCount > 0 && !isBatchRejectedView && (
        /* Its own line, where the old inline listing sat. Kept OUT of the
         * message row because a Button is whitespace-nowrap and unshrinkable:
         * as a sibling of the message it took a fixed slice of the row and
         * squeezed the message down to its longest single word. */
        <div className="flex flex-wrap items-center gap-2">
          {noteSuggestionsEnabled ? (
            unrecoverable.length > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-auto py-0.5 text-purple-700 underline dark:text-purple-300"
                onClick={() => setIsDroppedDialogOpen(true)}
              >
                {`${unrecoverable.length} suggestion${unrecoverable.length === 1 ? '' : 's'} couldn't be placed`}
              </Button>
            )
          ) : (
            <span className="text-purple-700 dark:text-purple-300">
              {`${droppedCount} could not be placed in the current document.`}
            </span>
          )}
          {noteSuggestionsEnabled && recovered.length > 0 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                for (const item of recovered) {
                  item.place?.()
                }
              }}
            >
              <IconRefresh className="size-3.5" />
              {`Place ${recovered.length} recovered suggestion${recovered.length === 1 ? '' : 's'}`}
            </Button>
          )}
        </div>
      )}
      {/* Closes itself once the last row is dismissed, since the summary control
       * that opened it is gone by then. */}
      {noteSuggestionsEnabled && (
        <DroppedSuggestionsDialog
          dropped={dropped}
          open={isDroppedDialogOpen && droppedCount > 0}
          onOpenChange={setIsDroppedDialogOpen}
          onDismiss={onReject}
        />
      )}
    </output>
  )
}
